import { StandardCheckbox, StandardText, useFetch, UseFetchOptions } from '@brandfolder/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import { boolean, object, SchemaOf } from 'yup';

import { FormFooter } from '@components/forms';
import { AsideContext } from '@context/aside';
import { BrandguideContext } from '@context/brandguide';
import { ThemeContext } from '@context/theme';
import { WhitelabelContext } from '@context/whitelabel';
import { ApiDatum } from '@typings/api';
import { Theme, ThemeColor } from '@typings/theme';

const schema: SchemaOf<ThemeColor> = object().shape({
  displayCmyk: boolean().defined(),
  displayHex: boolean().defined(),
  displayHsl: boolean().defined(),
  displayPantoneCoated: boolean().defined(),
  displayPantoneUncoated: boolean().defined(),
  displayRgb: boolean().defined()
});

interface ColorSettingsProps extends HTMLAttributes<HTMLFormElement> {}

const UnstyledColorSettings: FunctionComponent<ColorSettingsProps> = (props) => {
  const { setShowColorSettings } = useContext(AsideContext);
  const { brandfolder, brandguide, collection } = useContext(BrandguideContext);
  const { setTheme, theme } = useContext(ThemeContext);
  const { whitelabel } = useContext(WhitelabelContext);

  const associatedResource = collection
    ? `${collection.name} Collection`
    : `${brandfolder?.name || 'associated'} ${whitelabel.libraryName.singular}`; // Example Name Brandfolder || Example Name Library || associated Brandfolder || associated Library
  const note = `Colors are pulled from the ${associatedResource}. If color values don't display, review color values in the ${associatedResource}.`;

  const ref = useRef('');

  const { formState, handleSubmit, register, reset } = useForm<ThemeColor>({
    defaultValues: {
      ...theme.color
    },
    resolver: yupResolver(schema)
  });

  const { isDirty } = formState;

  const options: UseFetchOptions = {
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    method: 'PATCH',
    url: `/api/themes/${theme.key}`
  };

  const { error, fetch, loading, response } = useFetch<ApiDatum<Partial<Theme>>>(options);

  useEffect(() => {
    if (loading) {
      ref.current = toast.loading('Saving...');
    }
  }, [loading]);

  useEffect(() => {
    if (response) {
      const displayCmyk = response.data.attributes.color
        ? response.data.attributes.color.displayCmyk
        : theme.color.displayCmyk;

      const displayHex = response.data.attributes.color
        ? response.data.attributes.color.displayHex
        : theme.color.displayHex;

      const displayPantoneCoated =
        response.data.attributes.color?.displayPantoneCoated ?? theme.color.displayPantoneCoated;

      const displayPantoneUncoated =
        response.data.attributes.color?.displayPantoneUncoated ??
        theme.color.displayPantoneUncoated;

      const displayRgb = response.data.attributes.color
        ? response.data.attributes.color.displayRgb
        : theme.color.displayRgb;

      setTheme({
        ...theme,
        color: {
          ...theme.color,
          displayCmyk,
          displayHex,
          displayPantoneCoated,
          displayPantoneUncoated,
          displayRgb
        }
      });
      toast.dismiss(ref.current);
      toast.success('Saved!');

      reset({
        ...theme.color,
        displayCmyk,
        displayHex,
        displayPantoneCoated,
        displayPantoneUncoated,
        displayRgb
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (error) {
      toast.dismiss(ref.current);
      toast.error('Error saving.');
    }
  }, [error]);

  return (
    <form
      {...props}
      noValidate
      onSubmit={handleSubmit((values) => {
        const color: ThemeColor = {
          ...theme.color,
          ...values
        };

        fetch({
          ...options,
          body: {
            data: {
              attributes: {
                color
              }
            }
          }
        });
      })}
    >
      <div className="form__body">
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error */}
        <StandardCheckbox
          {...register('displayHex')}
          id="color-settings-display-hex"
          label="Display HEX"
        />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error */}
        <StandardCheckbox
          {...register('displayRgb')}
          id="color-settings-display-rgb"
          label="Display RGB"
        />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error */}
        <StandardCheckbox
          {...register('displayCmyk')}
          id="color-settings-display-cmyk"
          label="Display CMYK"
        />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error */}
        <StandardCheckbox
          {...register('displayPantoneCoated')}
          id="color-settings-display-pantone-coated"
          label="Display Pantone Coated"
        />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error */}
        <StandardCheckbox
          {...register('displayPantoneUncoated')}
          id="color-settings-display-pantone-uncoated"
          label="Display Pantone Uncoated"
        />
        <StandardText>
          <strong> Note:</strong> {note}
        </StandardText>
      </div>
      <FormFooter
        isDirty={isDirty}
        loading={loading}
        onCancel={() => setShowColorSettings(false)}
      />
    </form>
  );
};

const StyledColorSettings = styled(UnstyledColorSettings)`
  display: flex;
  flex-direction: column;
  flex: 1;

  .form__body {
    flex: 1;
    padding: 20px;
  }

  .bf-checkbox__standard {
    margin-bottom: 12px;

    &:last-of-type {
      margin-bottom: 20px;
    }
  }
`;

export const ColorSettings: FunctionComponent<ColorSettingsProps> = (props) => {
  return <StyledColorSettings {...props} />;
};
